import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogOffering, CatalogService, CatalogWarehouse } from './catalog.service';
import { InventoryCostingApiService, InventoryCostingPolicyChangeAudit } from './inventory-costing-api.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-costing-policy-detail',
  imports: [
    AinPageComponent, AinStateComponent, InfoHintComponent, RouterLink, FormsModule,
    ButtonModule, SelectModule, CheckboxModule, InputTextModule, InputNumberModule, TableModule,
    JalaliDateInputComponent, CurrencySelectorComponent, AinCanAccessDirective
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('catalog.costing.policies.detailDescription')">
      <ain-info-hint page-title-addon
        [titleKey]="'catalog.costing.policies.detailHelp.title'"
        [descriptionKey]="'catalog.costing.policies.detailHelp.description'"
        [bulletKeys]="['catalog.costing.policies.detailHelp.bullets.method', 'catalog.costing.policies.detailHelp.bullets.effective']" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('catalog.costing.states.noPermission')" [message]="t('catalog.costing.states.noPermissionMessage')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <a [routerLink]="['/inventory/costing/policies']" class="back-link">{{ t('catalog.action.backToList') }}</a>
          </div>

          <div class="form-grid">
            <label>
              <span>{{ t('catalog.costing.policies.method') }}</span>
              <p-select [(ngModel)]="form.method" [options]="methodOptions()" optionLabel="label" optionValue="value" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.inventory.offeringId') }}</span>
              <p-select [(ngModel)]="form.offeringId" [options]="offeringOptions()" optionLabel="label" optionValue="value" [showClear]="true" [filter]="true" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.inventory.warehouseId') }}</span>
              <p-select [(ngModel)]="form.warehouseId" [options]="warehouseOptions()" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.costing.policies.effectiveFrom') }}</span>
              <ain-date-input [(ngModel)]="form.effectiveFromUtc" />
            </label>
            <label>
              <span>{{ t('columns.currency') }}</span>
              <ain-currency-selector [(ngModel)]="form.currencyCode" />
            </label>
            <label>
              <span>{{ t('catalog.costing.policies.roundingScale') }}</span>
              <p-inputNumber [(ngModel)]="form.roundingScale" [min]="0" [max]="6" />
            </label>
            <label class="check">
              <p-checkbox [binary]="true" [(ngModel)]="form.hardBlockNegativeStock" />
              <span>{{ t('catalog.costing.policies.hardBlockNegativeStock') }}</span>
            </label>
            <label class="check">
              <p-checkbox [binary]="true" [(ngModel)]="form.isActive" />
              <span>{{ t('catalog.costing.policies.active') }}</span>
            </label>
            @if (!isNew()) {
              <label>
                <span>{{ t('catalog.costing.policies.changeReason') }}</span>
                <input pInputText [(ngModel)]="form.changeReason" />
              </label>
            }
          </div>

          <div class="actions" ainCanAccess [ainCanAccessPermission]="isNew() ? 'inventory.costing.manage' : 'inventory.costing.policy-elevated'">
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
          </div>
          @if (error()) { <p class="error">{{ error() }}</p> }
          @if (message()) { <p class="success">{{ message() }}</p> }

          @if (!isNew() && audits().length > 0) {
            <section class="audit-section">
              <h3>{{ t('catalog.costing.policies.auditTitle') }}</h3>
              <p-table [value]="audits()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.costing.policies.auditChangedAt') }}</th>
                    <th>{{ t('catalog.costing.policies.method') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('catalog.costing.policies.changeReason') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.changedAtUtc }}</td>
                    <td>{{ row.previousMethod }} → {{ row.newMethod }}</td>
                    <td>{{ row.previousIsActive ? t('catalog.costing.policies.active') : t('catalog.costing.policies.inactive') }} → {{ row.newIsActive ? t('catalog.costing.policies.active') : t('catalog.costing.policies.inactive') }}</td>
                    <td>{{ row.reason }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; background: #fff; }
    .toolbar { margin-bottom: 12px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 12px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid span { font-size: 12px; color: #475569; font-weight: 600; }
    .check { display: flex; align-items: center; gap: 8px; }
    .actions { margin-top: 12px; }
    .error { color: #b91c1c; }
    .success { color: #15803d; }
    .audit-section { margin-top: 16px; border-top: 1px solid #e2e8f0; padding-top: 12px; }
    .audit-section h3 { margin: 0 0 10px; font-size: 14px; }
  `]
})
export class InventoryCostingPolicyDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly costingApi = inject(InventoryCostingApiService);
  private readonly catalog = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly message = signal('');
  readonly permissionDenied = signal(false);
  readonly isNew = signal(true);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly audits = signal<InventoryCostingPolicyChangeAudit[]>([]);
  private originalMethod = '';
  private originalIsActive = true;

  methodOptions(): { label: string; value: string }[] {
    return [
      { label: this.t('catalog.costing.methods.MovingWac'), value: 'MovingWac' },
      { label: this.t('catalog.costing.methods.Fifo'), value: 'Fifo' },
      { label: this.t('catalog.costing.methods.Standard'), value: 'Standard' }
    ];
  }

  form = {
    method: 'MovingWac',
    offeringId: '',
    warehouseId: '',
    effectiveFromUtc: new Date().toISOString(),
    currencyCode: 'USD',
    roundingScale: 2,
    hardBlockNegativeStock: true,
    isActive: true,
    changeReason: ''
  };

  ngOnInit(): void {
    this.catalog.listOfferings().subscribe({ next: o => this.offerings.set(o.filter(x => x.inventoryEnabled)) });
    this.catalog.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    this.isNew.set(id === 'new');
    if (this.isNew()) {
      this.loading.set(false);
      return;
    }
    this.load(id);
  }

  pageTitle(): string {
    return this.isNew()
      ? this.t('catalog.costing.policies.createTitle')
      : this.t('catalog.costing.policies.editTitle');
  }

  load(id: string): void {
    this.loading.set(true);
    this.costingApi.getPolicy(id).subscribe({
      next: policy => {
        this.form = {
          method: policy.method,
          offeringId: policy.offeringId ?? '',
          warehouseId: policy.warehouseId ?? '',
          effectiveFromUtc: policy.effectiveFromUtc,
          currencyCode: policy.currencyCode,
          roundingScale: policy.roundingScale,
          hardBlockNegativeStock: policy.hardBlockNegativeStock,
          isActive: policy.isActive,
          changeReason: ''
        };
        this.originalMethod = policy.method;
        this.originalIsActive = policy.isActive;
        this.loadAudits(id);
        this.loading.set(false);
      },
      error: err => {
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('catalog.costing.states.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  loadAudits(id: string): void {
    this.costingApi.listPolicyChangeAudits(id).subscribe({
      next: rows => this.audits.set(rows),
      error: () => this.audits.set([])
    });
  }

  save(): void {
    this.saving.set(true);
    this.error.set('');
    this.message.set('');
    const payload = {
      offeringId: this.form.offeringId || null,
      warehouseId: this.form.warehouseId || null,
      method: this.form.method,
      effectiveFromUtc: this.form.effectiveFromUtc,
      currencyCode: this.form.currencyCode,
      roundingScale: this.form.roundingScale,
      hardBlockNegativeStock: this.form.hardBlockNegativeStock,
      isActive: this.form.isActive
    };
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    const needsElevated = !this.isNew() &&
      (this.form.method !== this.originalMethod || this.form.isActive !== this.originalIsActive);

    if (needsElevated && !this.form.changeReason.trim()) {
      this.saving.set(false);
      this.error.set(this.t('catalog.costing.policies.changeReasonRequired'));
      return;
    }

    const request$ = this.isNew()
      ? this.costingApi.createPolicy(payload)
      : needsElevated
        ? this.costingApi.changePolicyWithAudit(id, { ...payload, reason: this.form.changeReason.trim() })
        : this.costingApi.updatePolicy(id, payload);

    request$.subscribe({
      next: saved => {
        this.saving.set(false);
        this.message.set(this.t('catalog.costing.states.saved'));
        if (this.isNew()) {
          void this.router.navigate(['/inventory/costing/policies', saved.id]);
        } else {
          this.originalMethod = saved.method;
          this.originalIsActive = saved.isActive;
          this.form.changeReason = '';
          this.loadAudits(id);
        }
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('catalog.costing.states.saveFailed'));
      }
    });
  }

  offeringOptions = () => this.offerings().map(o => ({ label: `${o.code} — ${o.title}`, value: o.id }));
  warehouseOptions = () => this.warehouses().map(w => ({ label: `${w.code} — ${w.name}`, value: w.id }));

  t(key: string): string {
    return this.localization.translate(key);
  }
}
